import React, { useRef, useState, useEffect, useCallback } from "react";
import { autocompleteNote } from "../../../services/patient/carePlan";
import styles from "./NoteAutocomplete.module.css";

// 400ms sits above the endpoint's typical latency cadence: fewer requests
// fire mid-typing, which (with validate-on-arrival below) keeps the network
// tab almost free of superseded "(canceled)" entries.
const DEBOUNCE_MS = 400;
const MIN_CHARS = 3;

/**
 * "Replace the last word" contract (agreed with the ML team): the model
 * ALWAYS returns everything after the last whitespace of the input - the
 * user's partial word repeated EXACTLY as typed, plus the continuation.
 *   "...he has been assig" -> "assigned another medication"
 *   "patient is"           -> "is currently experiencing fatigue"
 *   "patient is " (space)  -> "currently experiencing fatigue" (empty token)
 * The client conceptually deletes the last word and pastes the response; the
 * join point is always a whitespace position, so no space guessing and no
 * "dis enrolled" / double-space joins are possible.
 *
 * Implemented as an append: because the response must start with the last
 * token verbatim, value + response.slice(lastToken.length) IS the
 * delete-and-paste result. Returns "" when there is nothing safe to show.
 */
export const computeRemainder = (value, suggestion) => {
  if (!suggestion || typeof suggestion !== "string") return "";
  const lastToken = (value || "").split(/\s/).pop(); // "" when value ends in whitespace
  // Typo-protection (mandatory per the contract): the model must repeat the
  // user's partial word verbatim. If it "fixed" a typo or rephrased
  // ("assing" -> "assigned..."), discard - never paste. The comparison is
  // case-INsensitive only because the live model capitalizes the echoed
  // token at sentence start ("dis" -> "Disenrolled..."); since we only ever
  // append the slice AFTER the token, the user's own casing is never
  // altered, and a respelled token still fails the check either way.
  if (!suggestion.toLowerCase().startsWith(lastToken.toLowerCase())) return "";
  let remainder = suggestion.slice(lastToken.length);
  // Defensive: the user already typed the whitespace, so a (misbehaving)
  // leading space in the response would produce a double space.
  if (lastToken === "") remainder = remainder.replace(/^[ \t]+/, "");
  return remainder;
};

/**
 * Drop-in replacement for a controlled <textarea> that adds Gmail-style inline
 * autocomplete: as the user types, a debounced call to /chatbot/autocomplete
 * fetches a completion that renders as greyed ghost text after the caret.
 * Pressing Tab (or Right Arrow at the end) accepts it; Escape dismisses it.
 *
 * It forwards value/onChange/className/style/placeholder/readOnly/rows and any
 * extra props to the native textarea, and passes className/style to a backing
 * "mirror" div so the overlay inherits the exact font/padding/border of each
 * site without per-site CSS.
 */
const NoteAutocompleteTextarea = ({
  value = "",
  onChange,
  className,
  style,
  placeholder,
  readOnly = false,
  disabled = false,
  enableAutocomplete = true,
  onKeyDown,
  onScroll,
  ...rest
}) => {
  const textareaRef = useRef(null);
  const mirrorRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const composingRef = useRef(false);
  const [remainder, setRemainder] = useState("");

  const active = enableAutocomplete && !readOnly && !disabled;

  const cancelPending = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const caretAtEnd = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return false;
    return (
      el.selectionStart === el.selectionEnd &&
      el.selectionStart === (el.value ? el.value.length : 0)
    );
  }, []);

  // Debounced fetch whenever the text changes.
  //
  // Cancellation policy: keystrokes do NOT abort the in-flight request.
  // Under the replace-last-word contract, a response requested for an older
  // text is often still valid when the user merely kept typing (or
  // backspaced within) the SAME word, so each response is validated against
  // the CURRENT textarea state on arrival instead. The only place an XHR is
  // superseded (-> "(canceled)" in devtools) is when the next request
  // actually fires (single-flight), which the longer debounce makes rare.
  useEffect(() => {
    // Any text change invalidates the current ghost and any pending
    // (not-yet-fired) debounce timer.
    setRemainder("");
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (!active) return undefined;
    if (composingRef.current) return undefined;
    if (value.trim().length < MIN_CHARS) return undefined;
    if (!caretAtEnd()) return undefined;

    debounceRef.current = setTimeout(() => {
      // Single-flight: supersede the previous request only now, when the
      // replacement is actually being sent.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestedFor = value;
      // Text up to and including the last whitespace at request time. The
      // response stays usable as long as the user only edits AFTER this
      // point without introducing new whitespace.
      const requestedHead = requestedFor.slice(
        0,
        requestedFor.length - requestedFor.split(/\s/).pop().length
      );

      autocompleteNote(requestedFor, { signal: controller.signal })
        .then((suggestion) => {
          const el = textareaRef.current;
          if (!el) return;
          const current = el.value;
          // Validate-on-arrival: the head must be untouched...
          if (!current.startsWith(requestedHead)) return;
          // ...and everything typed since must still be the same single
          // word (no new whitespace -> the join point is unchanged).
          if (/\s/.test(current.slice(requestedHead.length))) return;
          // Caret must still be at the end.
          if (
            !(
              el.selectionStart === el.selectionEnd &&
              el.selectionStart === current.length
            )
          ) {
            return;
          }
          // Recompute against the CURRENT token: if the user extended
          // "assig" to "assign" meanwhile, "assigned another medication"
          // still applies (ghost "ed another medication").
          const next = computeRemainder(current, suggestion);
          if (next) setRemainder(next);
        })
        .catch(() => {
          // Aborted or network error: stay silent, show no ghost.
        })
        .finally(() => {
          if (abortRef.current === controller) abortRef.current = null;
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [value, active, caretAtEnd]);

  // Abort any work on unmount.
  useEffect(() => cancelPending, [cancelPending]);

  const emitValue = useCallback(
    (nextValue) => {
      if (onChange) onChange({ target: { value: nextValue } });
    },
    [onChange]
  );

  const acceptSuggestion = useCallback(() => {
    if (!remainder) return false;
    const nextValue = value + remainder;
    setRemainder("");
    cancelPending();
    emitValue(nextValue);
    // Keep focus and drop the caret at the end after the value updates.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      try {
        el.setSelectionRange(nextValue.length, nextValue.length);
      } catch (e) {
        /* no-op */
      }
    });
    return true;
  }, [remainder, value, cancelPending, emitValue]);

  const handleKeyDown = (e) => {
    if (remainder) {
      if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        acceptSuggestion();
        if (onKeyDown) onKeyDown(e);
        return;
      }
      if (e.key === "ArrowRight" && caretAtEnd()) {
        e.preventDefault();
        acceptSuggestion();
        if (onKeyDown) onKeyDown(e);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setRemainder("");
        if (onKeyDown) onKeyDown(e);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  // Drop the ghost as soon as the caret leaves the end of the text.
  const handleCaretMove = () => {
    if (remainder && !caretAtEnd()) setRemainder("");
  };

  const handleChange = (e) => {
    setRemainder("");
    if (onChange) onChange(e);
  };

  const handleScroll = (e) => {
    if (mirrorRef.current && textareaRef.current) {
      mirrorRef.current.scrollTop = textareaRef.current.scrollTop;
      mirrorRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
    if (onScroll) onScroll(e);
  };

  const showGhost = active && !!remainder;

  return (
    <div className={styles.wrapper}>
      <div
        ref={mirrorRef}
        aria-hidden="true"
        className={`${className || ""} ${styles.mirror}`}
        style={{
          ...style,
          color: "transparent",
          borderColor: "transparent",
          fontFamily: "inherit",
          boxSizing: "border-box",
          // Reserve the scrollbar gutter so the mirror's text wraps at the
          // SAME width as the textarea once it overflows and shows a
          // scrollbar; otherwise the two layouts diverge and the ghost text
          // drifts/distorts on scroll.
          scrollbarGutter: "stable",
        }}
      >
        <span>{value}</span>
        {showGhost && <span className={styles.ghost}>{remainder}</span>}
        {showGhost && <span className={styles.tabHint}>Tab &#8677;</span>}
      </div>
      <textarea
        ref={textareaRef}
        className={className}
        style={{
          ...style,
          background: "transparent",
          backgroundColor: "transparent",
          position: "relative",
          zIndex: 1,
          boxSizing: "border-box",
          // Match the mirror: always reserve the scrollbar gutter so the
          // text width (and therefore wrapping) never changes when the
          // scrollbar appears, keeping the ghost overlay aligned 1:1.
          scrollbarGutter: "stable",
          // A textarea is inline-block by default, which leaves a baseline
          // descender gap below it; that makes the wrapper taller than the
          // textarea and the absolutely-positioned mirror (top:0;bottom:0)
          // stretch past it, drifting the ghost ~6px vertically on scroll.
          // block removes the gap so wrapper height == textarea height.
          display: "block",
        }}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleCaretMove}
        onClick={handleCaretMove}
        onSelect={handleCaretMove}
        onScroll={handleScroll}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={() => {
          composingRef.current = false;
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
        {...rest}
      />
    </div>
  );
};

export default NoteAutocompleteTextarea;
